import {
	CORE_PROJECT,
	SERVICE_DESK_PROJECT,
	SOFTWARE_PROJECT,
	PRODUCT_DISCOVERY_PROJECT,
	type ProjectType,
	CUSTOMER_SERVICE_PROJECT,
} from '@atlassian/jira-common-constants/src/project-types.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { serializeBackLink, type BackLink } from './backlink.tsx';
import { PACKAGE_NAME } from './consts.tsx';
import { toErrorId, toErrorMessage } from './types.tsx';
import { logError } from './util/error-logger.tsx';

// NOTE: consider using getJsmProjectSettingsUrl from src/packages/router-routes/project-settings instead, not to duplicate the logic
export const getNextgenSettingsUrl = (projectType: ProjectType, projectKey: string): string => {
	const products = {
		[CORE_PROJECT]: 'core',
		[SERVICE_DESK_PROJECT]: 'servicedesk',
		[CUSTOMER_SERVICE_PROJECT]: fg('jcs_project_type_m3') ? 'servicedesk' : 'customerservice',
		[SOFTWARE_PROJECT]: 'software',
		[PRODUCT_DISCOVERY_PROJECT]: 'polaris',
	};

	const product = products[projectType];
	if (!product) {
		logError(
			PACKAGE_NAME,
			toErrorId('urls'),
			toErrorMessage(`Can't build default project settings url for project type: ${projectType}`),
			new Error(`Unexpected project type: ${projectType}`),
		);

		// let's return classic url in that case
		return `/projects/${projectKey}/settings`;
	}

	if (projectType === PRODUCT_DISCOVERY_PROJECT) {
		return `/jira/${product}/projects/${projectKey}/ideas/settings`;
	}

	return `/jira/${product}/projects/${projectKey}/settings`;
};

export const getNextgenSettingsIssueTypesUrl = (
	projectType: ProjectType,
	projectKey: string,
	issueTypeId?: string,
	backLink?: BackLink,
): string => {
	if (projectType === PRODUCT_DISCOVERY_PROJECT) {
		return `${getNextgenSettingsUrl(projectType, projectKey)}`;
	}

	return `${getNextgenSettingsUrl(projectType, projectKey)}/issuetypes${
		issueTypeId ? `/${issueTypeId}` : ''
	}${serializeBackLink(backLink)}`;
};

export const getNextgenSettingsWorkflowUrl = (
	projectType: ProjectType,
	projectKey: string,
	issueTypeId: string,
): string => {
	if (projectType === PRODUCT_DISCOVERY_PROJECT) {
		return `${getNextgenSettingsUrl(projectType, projectKey)}/workflow`;
	}
	return `${getNextgenSettingsUrl(projectType, projectKey)}/issuetypes/${issueTypeId}/workflow`;
};

export const getNextgenSettingsJSDAutomationUrl = (projectKey: string): string =>
	`${getNextgenSettingsUrl(SERVICE_DESK_PROJECT, projectKey)}/automation`;

export const getClassicJSWSettingsUrl = (projectKey: string): string =>
	`/jira/software/c/projects/${projectKey}/settings`;

export const getProjectSettingsDetailsUrl = (
	projectType: ProjectType,
	projectKey: string,
	isSimplified: boolean,
): string => {
	const urlBase =
		projectType === SOFTWARE_PROJECT && !isSimplified
			? getClassicJSWSettingsUrl(projectKey)
			: getNextgenSettingsUrl(projectType, projectKey);

	return `${urlBase}/details`;
};

/**
 * For JSW CMP projects only.
 * Goes directly to "Issue layout" configuration page, a.k.a. SWIFT
 */
export const getClassicIssueLayoutSettingsUrl = ({
	projectKey,
	viewScreenId,
	backLink,
}: {
	projectKey: string;
	viewScreenId: number;
	backLink: BackLink | undefined;
}): string =>
	`/plugins/servlet/project-config/${projectKey}/issuelayout?screenId=${viewScreenId}${serializeBackLink(
		backLink,
	)}`;

/**
 * JSM CMP projects only.
 * Goes to Request type layout configuration page, a.k.a. SWIFT
 */
export const getClassicRequestTypeLayoutSettingsUrl = ({
	projectKey,
	requestTypeId,
	backLink,
}: {
	projectKey: string;
	requestTypeId: string | number;
	backLink: BackLink | undefined;
}): string =>
	`/jira/servicedesk/projects/${projectKey}/settings/request-types/request-type/${requestTypeId}${serializeBackLink(
		backLink,
	)}`;
